import axios from 'axios';
import type {
  FeatureSummary,
  MetricSummary,
  MetricDetail,
  ClientSummary,
  ClientDetail,
  CreditEntry,
  UsageActionEntry,
  PagedResult,
} from '@/types';

// All API calls go through the BFF on the same origin. Auth is the BFF's HttpOnly session
// cookie (sent via withCredentials). CSRF: SameSite=Strict on the session cookie is the
// primary defence; on top of that, axios reads the BFF-issued XSRF-TOKEN cookie and echoes
// it in the X-XSRF-TOKEN header, which the BFF validates on every mutating /api/Admin call.
const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: { 'Content-Type': 'application/json' },
});

// 401 from the BFF means the session is gone — kick the user through a fresh OIDC
// challenge by bouncing to /api/v1/auth/login. The BFF returns 401 only when the
// session cookie is missing or the user opted out, not on every transient backend
// hiccup, so this is a real terminal state, not a poll.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/') {
      const target = window.location.pathname + window.location.search;
      window.location.assign(`/api/v1/auth/login?returnUrl=${encodeURIComponent(target)}`);
    }
    return Promise.reject(error);
  }
);

// Backend wraps payloads in { value: T, isError, error, ... } via Response<T>.
// Strip that wrapper here; if isError is true, propagate as a thrown Error.
function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'isError' in payload) {
    const r = payload as { isError: boolean; error?: string; value?: T };
    if (r.isError) throw new Error(r.error ?? 'Backend error');
    return r.value as T;
  }
  return payload as T;
}

// Identity moved to /api/v1/auth/user (the BFF) — `useBffAuth()` exposes it; the
// old `adminApi.me` / `AdminMe` are gone.

export const adminApi = {
  features: () => api.get('/Admin/Features').then(r => unwrap<FeatureSummary[]>(r.data)),
  metrics: () => api.get('/Admin/Metrics').then(r => unwrap<MetricSummary[]>(r.data)),
  metricDetail: (token: string) =>
    api.get(`/Admin/Metrics/${encodeURIComponent(token)}`).then(r => unwrap<MetricDetail>(r.data)),
  clients: (page = 1, pageSize = 50) =>
    api.get('/Admin/Clients', { params: { page, pageSize } })
       .then(r => unwrap<PagedResult<ClientSummary>>(r.data)),
  clientDetail: (clientId: number) =>
    api.get(`/Admin/Clients/${clientId}`).then(r => unwrap<ClientDetail>(r.data)),
  credits: (clientId?: number, featureToken?: string) =>
    api.get('/Admin/Credits', { params: { clientId, featureToken } })
       .then(r => unwrap<CreditEntry[]>(r.data)),
  addCredits: (body: {
    clientId: number;
    featureToken: string;
    amount: number;
    cycleType?: number;
    creditsPerCycle?: number;
    cycleStart?: string;
  }) =>
    api.post('/Admin/Credits', body).then(r => unwrap<CreditEntry>(r.data)),
  usageActions: (filters: {
    clientId?: number;
    featureToken?: string;
    from?: string;
    toExclusive?: string;
    page?: number;
    pageSize?: number;
  }) =>
    api.get('/Admin/UsageActions', { params: filters })
       .then(r => unwrap<PagedResult<UsageActionEntry>>(r.data)),
};

export default api;
